import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Logo from "@/components/founding/Logo";
import axios from "axios";
import { accessTokenFrom, storeAuthToken } from "@/lib/authToken";
import { FOUNDING_ENTRY_PATH } from "@/lib/foundingEntry";
import { apiBase } from "@/lib/apiBase";
import { PasswordInput } from "@/components/ui/password-input";

const API = apiBase();

async function loginRequest(email, password) {
    const params = new URLSearchParams();
    params.append("username", email);
    params.append("password", password);
    const r = await axios.post(`${API}/auth/login`, params);
    return r.data;
}

export default function SignupModal({ open, onOpenChange, mode: modeProp, onModeChange }) {
    const navigate = useNavigate();
    const resetTimer = useRef(null);
    const [uncontrolledMode, setUncontrolledMode] = useState(
        modeProp === "login" ? "login" : "signup"
    );
    const mode = modeProp === "login" || modeProp === "signup" ? modeProp : uncontrolledMode;
    const setMode = (next) => {
        if (modeProp !== "login" && modeProp !== "signup") setUncontrolledMode(next);
        if (onModeChange) onModeChange(next);
    };
    const [submitting, setSubmitting] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        license: "",
        company: "",
    });

    useEffect(() => () => {
        if (resetTimer.current) clearTimeout(resetTimer.current);
    }, []);

    const handleChange = (k) => (e) =>
        setForm((s) => ({ ...s, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (mode === "signup" && !termsAccepted) {
            toast.error("You must agree to the Terms of Service.");
            return;
        }

        setSubmitting(true);
        try {
            if (mode === "signup") {
                const signupRes = await axios.post(`${API}/auth/signup`, {
                    email: form.email,
                    password: form.password,
                    full_name: form.name,
                    firm: form.company,
                    license_number: form.license,
                });
                // Signup returns the profile, not a session. Use a token if
                // one is ever added; otherwise log in with the same password.
                let token = accessTokenFrom(signupRes.data);
                if (!token) {
                    token = accessTokenFrom(
                        await loginRequest(form.email, form.password)
                    );
                }
                if (!token) {
                    throw new Error("Account created, but login did not return a token.");
                }
                storeAuthToken(token);
                toast.success("Account created! Check your email for onboarding.");
                onOpenChange(false);
                navigate(FOUNDING_ENTRY_PATH);
            } else {
                const token = accessTokenFrom(
                    await loginRequest(form.email, form.password)
                );
                if (!token) {
                    throw new Error("Login did not return a token.");
                }
                storeAuthToken(token);
                toast.success("Welcome back, Engineer.");
                onOpenChange(false);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Authentication failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = (next) => {
        onOpenChange(next);
        if (!next) {
            if (resetTimer.current) clearTimeout(resetTimer.current);
            resetTimer.current = setTimeout(() => {
                setForm({ name: "", email: "", password: "", license: "", company: "" });
                setTermsAccepted(false);
            }, 250);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="!rounded-none !p-0 !max-w-lg !border-white/15 !bg-black !shadow-[0_0_80px_-20px_rgba(239,68,68,0.3)]">
                <div className="relative p-7">
                    <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-red-500" />
                    <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-red-500" />

                    <div className="mb-8 flex items-center justify-between">
                        <Logo size={28} />
                        <button
                            type="button"
                            onClick={() => setMode(mode === "signup" ? "login" : "signup")}
                            className="fsrs-auth-mode-toggle text-red-500 fsrs-mono text-sm hover:underline"
                            aria-label={mode === "signup" ? "Already registered? Log in" : "Need an account? Sign up"}
                        >
                            {mode === "signup" ? "ALREADY REGISTERED? LOG IN" : "NEED AN ACCOUNT? SIGN UP"}
                        </button>
                    </div>

                    <DialogTitle className="fsrs-heading text-white text-3xl mb-2">
                        {mode === "signup" ? "Create your profile." : "Login to Workbench."}
                    </DialogTitle>
                    <DialogDescription className="text-slate-400 mb-8 fsrs-mono text-base">
                        {mode === "signup" ? "$249/month · Includes 15 SRUs each month" : "Secure access for licensed professionals."}
                    </DialogDescription>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {mode === "signup" && (
                            <Field label="FULL NAME">
                                <input type="text" value={form.name} onChange={handleChange("name")} placeholder="Jane Doe, P.E." className="fsrs-input" required />
                            </Field>
                        )}
                        <Field label="WORK EMAIL">
                            <input type="email" value={form.email} onChange={handleChange("email")} placeholder="jane@firmname.com" className="fsrs-input" required />
                        </Field>
                        <Field label="PASSWORD">
                            <PasswordInput
                                key={mode}
                                value={form.password}
                                onChange={handleChange("password")}
                                placeholder="••••••••"
                                required
                            />
                        </Field>

                        {mode === "signup" && (
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="FIRM (OPTIONAL)">
                                    <input type="text" value={form.company} onChange={handleChange("company")} placeholder="Firm name" className="fsrs-input" />
                                </Field>
                                <Field label="LICENSE #">
                                    <input type="text" value={form.license} onChange={handleChange("license")} placeholder="State + #" className="fsrs-input" />
                                </Field>
                            </div>
                        )}

                        {mode === "signup" && (
                            <div className="bg-red-500/5 border border-white/10 p-4 flex gap-3">
                                <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="mt-1 h-5 w-5" />
                                <span className="text-slate-300 text-sm">Agree to <Link to="/terms" target="_blank" className="underline">Terms & Disclaimer</Link></span>
                            </div>
                        )}

                        <button type="submit" disabled={submitting} className="fsrs-cta w-full justify-center">
                            {submitting ? <Loader2 className="animate-spin" /> : (mode === "signup" ? "Create Account" : "Login Now")}
                        </button>
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="fsrs-label text-slate-500 mb-1.5 block text-xs">{label}</span>
            {children}
        </label>
    );
}
